/**
 * OAuth Callback Landing Page (Story 4.5, Task 6)
 *
 * Minimal page that receives OAuth result from the server redirect and
 * communicates it back to the opener window via postMessage (popup flow)
 * or redirects to the client page (redirect/mobile flow).
 *
 * Security:
 * - postMessage uses explicit targetOrigin (never '*')
 * - sentRef guard prevents duplicate sends
 * - No external resources (images, fonts, analytics)
 * - Referrer-Policy: no-referrer
 */

import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { parseCatalogCount } from "../../lib/parse-catalog-count";

// ============================================================================
// Route Definition
// ============================================================================

export interface OAuthCallbackSearch {
  result?: string;
  provider?: string;
  platforms?: string;
  existing?: string;
  error?: string;
  errorCategory?: string;
  clientId?: string;
  origin?: string;
  partial?: string;
  /**
   * Story 32-3 (AC #11) — cataloged-resource count from multi-tenant
   * callbacks (31-29). Count only, NEVER the catalog. Malformed values are
   * dropped (non-fatal by the 31-29 callback contract) — see
   * lib/parse-catalog-count.ts.
   */
  catalogCount?: number;
}

export const Route = createFileRoute("/oauth/callback")({
  validateSearch: (search: Record<string, unknown>): OAuthCallbackSearch => ({
    result: typeof search.result === "string" ? search.result : undefined,
    provider: typeof search.provider === "string" ? search.provider : undefined,
    platforms:
      typeof search.platforms === "string" ? search.platforms : undefined,
    existing:
      typeof search.existing === "string" ? search.existing : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
    errorCategory:
      typeof search.errorCategory === "string"
        ? search.errorCategory
        : undefined,
    clientId:
      typeof search.clientId === "string" ? search.clientId : undefined,
    origin: typeof search.origin === "string" ? search.origin : undefined,
    partial: typeof search.partial === "string" ? search.partial : undefined,
    catalogCount: parseCatalogCount(search.catalogCount),
  }),
  component: OAuthCallbackPage,
});

// ============================================================================
// Component
// ============================================================================

function OAuthCallbackPage() {
  const {
    result,
    provider,
    platforms,
    existing,
    error,
    errorCategory,
    clientId,
    origin,
    catalogCount,
  } = Route.useSearch();

  const sentRef = useRef(false);

  useEffect(() => {
    // Set security headers via meta tags
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (sentRef.current) return;

    const isSuccess = result === "success";
    // SECURITY: Always use window.location.origin — never from query params (AC #6)
    const targetOrigin = window.location.origin;

    // Build the payload
    const payload = isSuccess
      ? {
          type: "oauth-success" as const,
          provider: provider || "google",
          platforms: platforms ? platforms.split(",") : [],
          ...(existing ? { existing: existing.split(",") } : {}),
          // Story 32-3: numeric count only (schema-gated — a missing field
          // validates fine; the catalog itself NEVER travels here).
          ...(catalogCount !== undefined ? { catalogCount } : {}),
        }
      : {
          type: "oauth-error" as const,
          provider: provider || "google",
          error: error || "An unexpected error occurred",
          ...(errorCategory ? { errorCategory } : {}),
        };

    // Popup flow: send postMessage to opener
    if (window.opener) {
      sentRef.current = true;
      window.opener.postMessage(payload, targetOrigin);
      setTimeout(() => {
        window.close();
      }, 100);
      return;
    }

    // Redirect flow (full-page redirect, e.g., Mailchimp COOP or mobile):
    // Navigate to the client page with the result.
    sentRef.current = true;
    if (isSuccess && clientId) {
      const redirectUrl = new URL(
        `/clients/${clientId}`,
        window.location.origin
      );
      redirectUrl.searchParams.set("oauth", "success");
      if (platforms) redirectUrl.searchParams.set("platforms", platforms);
      // Story 32-3: the full-page-redirect path learns the count too, so the
      // client page can offer the catalog picker (count only — 31-29 AC #6).
      if (catalogCount !== undefined) {
        redirectUrl.searchParams.set("catalogCount", String(catalogCount));
      }
      window.location.href = redirectUrl.toString();
    } else if (!isSuccess && clientId) {
      const redirectUrl = new URL(
        `/clients/${clientId}`,
        window.location.origin
      );
      redirectUrl.searchParams.set("oauth", "error");
      if (error) redirectUrl.searchParams.set("reason", error);
      window.location.href = redirectUrl.toString();
    } else {
      // No clientId — redirect to dashboard
      window.location.href = isSuccess ? "/" : "/?oauth=error";
    }
  }, [
    result,
    provider,
    platforms,
    existing,
    error,
    errorCategory,
    clientId,
    origin,
    catalogCount,
  ]);

  // Minimal UI while processing
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">
          {result === "success"
            ? "Connection successful! Closing..."
            : result === "error"
              ? "Processing..."
              : "Completing authorization..."}
        </p>
      </div>
    </div>
  );
}
